'use client';

import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import { InteractiveExplainerButton } from '@/components/how-it-works/interactive-explainer-button';
import {
  Award01Icon,
  Coins01Icon,
  JusticeScale01Icon,
  Search02Icon,
  TradeUpIcon,
} from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { useSimulatedTradeModalStore } from '@/stores/simulated-trade-modal';

interface MarketsHowItWorksModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the "What is graduation?" sub-modal from an inline cross-link. */
  onWhatIsGraduation?: () => void;
  /** Opens the "Preventing manipulation" sub-modal from an inline cross-link. */
  onPreventManipulation?: () => void;
}

export function MarketsHowItWorksModal({
  open,
  onOpenChange,
  onWhatIsGraduation,
  onPreventManipulation,
}: MarketsHowItWorksModalProps): React.ReactNode {
  const t = useTranslations('markets_how_it_works');
  const openSimulatedTrade = useSimulatedTradeModalStore((s) => s.setOpen);

  return (
    <InfoModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('title')}
      description={t('description')}
      footer={
        <InfoModal.Footer
          cta={t('got_it')}
          onClose={(): void => onOpenChange(false)}
          lead={
            <InteractiveExplainerButton
              onOpen={(): void => {
                onOpenChange(false);
                openSimulatedTrade(true);
              }}
            />
          }
        />
      }
    >
      <InfoModal.IconList>
        <InfoModal.IconItem
          icon={Search02Icon}
          title={t('browse_title')}
          description={t('browse_description')}
        />
        <InfoModal.IconItem
          icon={Coins01Icon}
          title={t('trade_title')}
          description={t('trade_description')}
        />
        <InfoModal.IconItem
          icon={JusticeScale01Icon}
          title={t('resolve_title')}
          description={t('resolve_description')}
        />
        <InfoModal.IconItem
          icon={Award01Icon}
          title={t('earn_title')}
          description={t('earn_description')}
        />
        <InfoModal.IconItem
          icon={TradeUpIcon}
          title={t('graduate_title')}
          description={t('graduate_description')}
        />
      </InfoModal.IconList>
      <DocsLinks
        docsPath="/concepts/opinion-markets"
        crossLinks={[
          ...(onWhatIsGraduation !== undefined
            ? [{ key: 'graduation', label: t('graduation_link'), onClick: onWhatIsGraduation }]
            : []),
          ...(onPreventManipulation !== undefined
            ? [
                {
                  key: 'manipulation',
                  label: t('manipulation_link'),
                  onClick: onPreventManipulation,
                },
              ]
            : []),
        ]}
      />
    </InfoModal>
  );
}
